import {IDeferredComponent, Component, Bind, Ref, PreventDefault, LogExceptions, Trace, LogLevel, interpolate, HttpClient} from "xm-ui";
import {IMessengerOptions} from "./IMessengerOptions";
import {MessengerReplyComponent} from "./MessengerReplyComponent";
import {IMessengerApiResponse} from "./IMessengerApiResponse";
import {Validator} from "../validation/Validator";
import {PluginRepository} from "../plugins/PluginRepository";
import {registerError} from "../utils/registerError";

export class MessengerComponent extends Component<IMessengerOptions> {
    @Ref('.author') authorComponent: JQuery;
    @Ref('.content') contentContainer: JQuery;
    @Ref('.composer') composerComponent: JQuery;

    private parentComponent: IDeferredComponent;

    private loading: boolean;

    protected render(childNodes: Promise<JQuery>[]): Promise<JQuery> {
        let authorComponent = this.hasAuthor()
            ? (
                <header class="author">
                    <div class="author-avatar"><img src={this.options.response!.Author.ImageUrl}/></div>
                    <div class="author-name">{this.options.response!.Author.Name}</div>
                </header>
            )
            : null;

        let replyComponent = this.canBeReplied()
            ? <MessengerReplyComponent {...this.options.reply}/>
            : null;

        return (
            <main class={this.cls("container", "container-" + (this.options.response!.Type || "message"))}>
                {this.renderStyles()}
                <button class="btn-close" data-action="hide">✕</button>
                {authorComponent}
                <section class={this.cls("content", !replyComponent ? "content-without-reply" : "")}>
                    {this.getContent()}
                </section>
                {replyComponent}
            </main>
        );
    }

    @LogExceptions()
    @Trace(LogLevel.Info)
    protected async onMount() {
        await this.loadPlugins();

        this.parentComponent.ready(async () => {
            await this.afterContentChanged();

            if (this.soundsDisabled())
                return;

            let beep = document.createElement("audio");

            if (beep.canPlayType && beep.canPlayType(this.options.soundFormat!)) {
                beep.src =  this.options.sound!;
                beep.play();
            }
        });
    }

    protected extendOptions(extendedOptions?: IMessengerOptions) {
        super.extendOptions(extendedOptions);

        if (this.options.reply) {
            this.options.reply = $.extend({}, this.options.reply, {
                key: this.options.response!.LabelName,
                attributes: {
                    Template: this.options.response!.TemplateId
                }
            });
        }
    }

    protected getDefaultOptions(): Partial<IMessengerOptions> {
        return $.extend(super.getDefaultOptions(), {
            data: {},
            sound: "https://cdn.millionlakes.com/sounds/flop.mp3",
            soundFormat: "audio/mp3",
            fitToContent: true,
            errorText: 'An error occurred. Please try again later',
            pluginsDisabled: true
        });
    }

    protected getEventTarget(): JQuery {
        return this.parentComponent.getHostElement();
    }

    private renderStyles() {
        if (!this.options.response!.Styles)
            return '';

        return (
            <style>
                {this.options.response!.Styles}
            </style>
        );
    }

    private async afterContentChanged() {
        if (this.options.fitToContent) {
            let height = await this.calculateMessengerHeight();
            this.fireEvent("resized", height);
        } else {
            this.fireEvent("resized");
        }

        this.updateContentAnchors();
    }

    private getContent() {
        if (!this.options.data)
            return this.options.response!.Content;

        return interpolate(this.options.response!.Content, this.options.data);
    }

    private async calculateMessengerHeight(): Promise<number> {
        let dummy = this.contentContainer
            .clone();

        let container = this.contentContainer.parent();

        this.prepareHostElementForSizeCalculation(dummy, container);

        container.append(dummy);

        return new Promise<number>(resolve => {
            let images = dummy.find("img");
            let loadedCount = 0;

            let calculate = () => {
                let contentHeight = dummy.outerHeight();

                dummy.remove();

                return contentHeight
                    + this.authorComponent.outerHeight()
                    + this.composerComponent.outerHeight();
            };

            if (images.length > 0) {
                images.on("load error", () => {
                    if (++loadedCount < images.length)
                        return;

                    resolve(calculate());
                });
            } else {
                resolve(calculate());
            }
        });
    }

    private canBeReplied() {
        return this.options.response!.Type === 'message'
            && this.options.response!.ShowReply
            && this.options.reply
            && this.options.reply.api
            && this.options.reply.api.baseAddress
            && this.options.reply.user
            && this.options.reply.user.id;
    }

    private hasAuthor() {
        return this.options.response && this.options.response.Author;
    }

    private soundsDisabled() {
        return !this.options.response || !this.options.response.SoundEffect || !this.options.soundFormat || !this.options.sound;
    }

    private async loadPlugins() {
        if (this.options.pluginsDisabled || !this.options.response || !this.options.response.Scripts)
            return;

        for (let pluginName of this.options.response.Scripts) {
            try {
                let plugin = await PluginRepository.loadPlugin(pluginName);
                await plugin.plug(this);
            }
            catch (e) {
                registerError(e);
            }
        }
    }

    private async formSubmitted() {
        let apiResponse = this.options.response as IMessengerApiResponse;

        if (apiResponse.SubmitRedirectUrl) {
            window.top.location.href = apiResponse.SubmitRedirectUrl;
            return;
        }

        if (apiResponse.SubmitResponse) {
            this.contentContainer.html(apiResponse.SubmitResponse);
            await this.afterContentChanged();
        }
    }

    private updateContentAnchors() {
        this.getHostElement().find('a:not([target])').each((i, anchor) => {
            $(anchor).attr('target', '_top');
        });
    }

    @PreventDefault()
    @Bind("click", "[data-action='hide']")
    private onCloseButtonClick() {
        this.fireEvent('hide');
    }

    @Bind("click", "a,button")
    private onInternalClick(e: JQueryEventObject) {
        this.fireEvent('internal_click', e);
    }

    @Bind("resized", ".composer")
    private onComposerResized(e: JQueryEventObject, composerHeight: number) {
        this.contentContainer.css('bottom', composerHeight);
    }

    @Bind("submit", "form")
    @Bind("click", "[data-action='submit']")
    @Trace(LogLevel.Info)
    private async formSubmit(e: JQueryEventObject) {
        e.preventDefault();

        if (this.loading)
            return;

        const form = $(e.target).closest('form');
        const previousValidationResult = form.data('validationResult');

        if (previousValidationResult)
            previousValidationResult.clear(form);

        form.find('.global-error').remove();

        const validator = new Validator(this.options.response!.ValidationRules);
        const validationResult = validator.validate(form);

        if (!validationResult.isValid) {
            validationResult.mark(form);
            await this.afterContentChanged();
            return;
        }

        this.loading = true;

        try {
            let http = new HttpClient();

            let apiUrl = interpolate(this.options.apiUrl!, {
                action: 'PostTemplateFormData'
            });

            let data = $.extend({}, this.options.initParams, {
                templateId: this.options.response!.TemplateId,
                properties: form.serializeArray().reduce((properties, field) => {
                    properties[field.name] = field.value;
                    return properties;
                }, {} as any)
            });

            await http.post(apiUrl, data);

            await this.formSubmitted();

            this.loading = false;
        } catch (e) {
            registerError(e);

            $('<p></p>', {
                "class": "global-error error",
                text: this.options.errorText
            }).prependTo(form);

            await this.afterContentChanged();

            this.loading = false;
        }
    }
}



// WEBPACK FOOTER //
// ./components/MessengerComponent.tsx